import React, { useState, useContext } from "react";
import { Modal, Input, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { ENDPOINT } from "../config";
import { AuthenticatedApiContext } from "../../context/AuthenticatedApiContext";
import "./AddChildEntityModal.css";

const BuildingIcon = () => (
  <svg
    style={{ width: "20px", height: "20px", color: "#7c3aed" }}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

export const AddChildEntityModal = ({
  isOpen,
  onClose,
  processId,
  onSuccess,
  setFeedback,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const { get, post } = useContext(AuthenticatedApiContext);

  const handleSearch = async (value) => {
    const term = value || searchTerm;
    if (term.length < 2) {
      setError("Please enter at least 2 characters.");
      return;
    }
    setIsSearching(true);
    setError(null);
    setSelectedChild(null);
    try {
      const results = await get(
        `${ENDPOINT}/search-processes/${encodeURIComponent(term)}`,
      );
      setSearchResults(results);
      if (results.length === 0) {
        setError("No companies found matching your search.");
      }
    } catch (err) {
      setError("Failed to search. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirm = async () => {
    if (!selectedChild) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await post(
        `${ENDPOINT}/processes/${processId}/add-child`,
        { childCompanyId: selectedChild.company_id },
      );
      if (response.error) {
        setError(response.error);
        setIsSubmitting(false);
        return;
      }
      setFeedback({
        type: "success",
        message: `"${selectedChild.company_name}" has been added as a child entity.`,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(
        err.errors || "Failed to add child entity. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchTerm("");
    setSearchResults([]);
    setSelectedChild(null);
    setError(null);
    onClose();
  };

  return (
    <Modal
        visible={isOpen}
        onCancel={handleClose}
        centered
        destroyOnClose
        width={520}
        className="child-entity-modal"
        bodyStyle={{ padding: "24px" }}
        title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "4px 0",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              backgroundColor: "#f5f3ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BuildingIcon />
          </div>
          <span
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#111827",
            }}
          >
            Add child entity
          </span>
        </div>
      }
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            padding: "4px 0",
          }}
        >
          <Button onClick={handleClose} style={{ borderRadius: "10px", height: "38px", padding: "0 20px" }}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleConfirm}
            disabled={!selectedChild || isSubmitting}
            loading={isSubmitting}
            style={{
              borderRadius: "10px",
              height: "38px",
              padding: "0 20px",
              backgroundColor:
                !selectedChild || isSubmitting ? undefined : "#7c3aed",
              borderColor:
                !selectedChild || isSubmitting ? undefined : "#7c3aed",
            }}
          >
            {isSubmitting ? "Adding..." : "Confirm"}
          </Button>
        </div>
      }
    >
      <p
        style={{
          fontSize: "14px",
          color: "#6b7280",
          margin: "0 0 20px 0",
          lineHeight: "1.6",
        }}
      >
        Search for a company by name or company ID to add it as a child entity
        of this company.
      </p>

      {/* Search Input */}
      <Input.Search
        placeholder="Company name or company ID..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        onSearch={handleSearch}
        enterButton={
          <Button
            type="primary"
            icon={<SearchOutlined />}
            style={{
              backgroundColor: "#7c3aed",
              borderColor: "#7c3aed",
            }}
          >
            Search
          </Button>
        }
        loading={isSearching}
        size="medium"
        style={{ marginBottom: "16px" }}
      />

      {/* Error Message */}
      {error && (
        <div
          style={{
            padding: "10px 14px",
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            color: "#dc2626",
            fontSize: "13px",
            marginBottom: "16px",
          }}
        >
          {error}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: "10px",
            overflow: "hidden",
            maxHeight: "240px",
            overflowY: "auto",
          }}
        >
          {searchResults.map((process, index) => {
            const isSelected =
              selectedChild && selectedChild.id === process.id;
            const isHovered = hoveredId === process.id;
            return (
              <div
                key={process.id}
                onClick={() => setSelectedChild(process)}
                onMouseEnter={() => setHoveredId(process.id)}
                onMouseLeave={() => setHoveredId(null)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  cursor: "pointer",
                  backgroundColor: isSelected
                    ? "#f5f3ff"
                    : isHovered
                      ? "#fafafa"
                      : "white",
                  borderBottom:
                    index < searchResults.length - 1
                      ? "1px solid #f3f4f6"
                      : "none",
                  borderLeft: isSelected
                    ? "3px solid #7c3aed"
                    : "3px solid transparent",
                  transition: "all 0.15s ease",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: isSelected ? "#7c3aed" : "#111827",
                    }}
                  >
                    {process.company_name}
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#9ca3af",
                      fontFamily: "monospace",
                      marginTop: "2px",
                    }}
                  >
                    {process.company_id}
                  </div>
                </div>
                {isSelected && (
                  <svg
                    style={{
                      width: "18px",
                      height: "18px",
                      color: "#7c3aed",
                      flexShrink: 0,
                    }}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            );
          })}
        </div>
      )}

      
    </Modal>
  );
};
